import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import validerDemande from '../validerFormulaire';

interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite?: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormUK: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres passés via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États initiaux (issus des paramètres ou valeurs par défaut)
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const urlFlag = state?.url_flag || '';
  const validite = state?.validite || '';
  const commentaires = state?.commentaires || '';

  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<any[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');

  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);
  const [hideFees] = useState<boolean>(false);

  const total = prixconsulat + prixintervention; // Calcul du prix total

  // Mise à jour des champs non remplis selon les valeurs saisies
  const updateChampsNonRemplis = () => {
    const champs: string[] = [];

    if (!selectedVoyageur) champs.push(t('common.validation.traveler'));
    if (!dateDepart.trim()) champs.push(t('common.validation.departureDate'));
    if (!dateSortie.trim()) champs.push(t('common.validation.returnDate'));
    if (!assuranceVisa.trim()) champs.push(t('common.validation.insurance'));
    if (!sliderValue) champs.push(t('common.validation.cguAcceptance'));
    setChampsNonRemplis(champs);
  };

  useEffect(() => {
    updateChampsNonRemplis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVoyageur,
    dateDepart,
    dateSortie,
    assuranceVisa,
    sliderValue,
  ]);

  // Vérification globale du formulaire
  const isFormValid = champsNonRemplis.length === 0;

  // Récupération de la liste des voyageurs depuis le localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true);
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(
    (v: any) => v.id.toString() === selectedVoyageur
  );

  // Gestion d'une éventuelle sortie de page (avant unload)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () =>
      window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Fonction de formatage de la date au format jj/mm/aaaa
  const formatDate = (input: string): string => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  const handleBack = () => {
    if (
      window.confirm(t('common.confirmation.unsavedChanges'))
    ) {
      navigate(-1);
    }
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }
      const etatFormulaire = isFormValid;

      const data: any = {
        etatFormulaire,
        idUtilisateur: userId,
        idVoyageur: selectedVoyageur,
        titreVisa: title,
        dateDepart,
        dateSortie,
        prixconsulat,
        prixintervention,
        assuranceVisa,
        validite,
      };

      const response = await validerDemande(data);
      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.idDemande) {
        setCreatedDemandeId(response.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error("Erreur lors de la validation de la demande:", error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
  };

  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors societe) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Creer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {tp('uk.fees.totalPrice', { total })}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur (si pas en mode édition) */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map((voyageur: any) => (
                    <MenuItem key={voyageur.id} value={voyageur.id.toString()}>
                      {voyageur.prenom} {voyageur.nom}
                      {voyageur.numeropax ? t('common.selectTravelerWithPassport', { passport: voyageur.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          <TextField
            label={t('common.departureDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.label')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormUK;
